import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Send, Mail, CheckCircle2, AlertTriangle, Clock, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  delivered: { label: "Livré", cls: "bg-emerald-500/15 text-emerald-400", icon: CheckCircle2 },
  sent: { label: "Envoyé", cls: "bg-sky-500/15 text-sky-400", icon: Send },
  bounced: { label: "Rejeté", cls: "bg-red-500/15 text-red-400", icon: AlertTriangle },
  complained: { label: "Plainte", cls: "bg-orange-500/15 text-orange-400", icon: AlertTriangle },
  delayed: { label: "Retardé", cls: "bg-amber-500/15 text-amber-400", icon: Clock },
  failed: { label: "Échec", cls: "bg-red-500/15 text-red-400", icon: AlertTriangle },
  pending: { label: "En attente", cls: "bg-white/10 text-muted-foreground", icon: Clock },
};
const KIND_FR = {
  verification: "Vérification", approval: "Approbation", reset: "Réinitialisation",
  bootstrap: "Bootstrap", test: "Test", weekly_report: "Rapport hebdo", generic: "Générique",
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export default function AdminEmail() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = useState("preview");

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold tracking-tight">E-mails</h1>
      <p className="mt-1 text-sm text-muted-foreground">Aperçu des modèles, tests d'envoi et suivi de livraison Resend.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {[["preview", "Aperçu des modèles"], ["delivery", "Livraison"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`email-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "preview" ? <PreviewTab defaultEmail={user?.email} /> : <DeliveryTab isSuper={isSuper} />}
    </div>
  );
}

function PreviewTab({ defaultEmail }) {
  const [selected, setSelected] = useState(0);
  const [to, setTo] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => (await api.get("/admin/email/templates")).data,
  });

  const sendTest = async () => {
    const tpl = templates?.[selected];
    if (!tpl) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/test-send", { kind: tpl.kind, to });
      toast.success(`Test envoyé à ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally { setSending(false); }
  };

  if (isLoading) return <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const tpl = templates?.[selected];

  return (
    <div className="mt-6 grid gap-5 lg:grid-cols-[260px_1fr]">
      <div className="space-y-2">
        {templates.map((t, i) => (
          <button key={t.kind} onClick={() => setSelected(i)} data-testid={`email-template-${t.kind}`}
            className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
              i === selected ? "border-primary/40 bg-primary/10 text-foreground" : "border-white/5 bg-white/5 text-muted-foreground hover:text-foreground"}`}>
            <Mail className="h-4 w-4 shrink-0" /> {t.label}
          </button>
        ))}
        <div className="mt-4 rounded-lg border border-white/5 bg-[hsl(var(--surface-elevated))] p-3">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Envoyer un test à</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to"
            className="mt-2 w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-primary/40" placeholder="votre@email.com" />
          <button onClick={sendTest} disabled={sending} data-testid="email-test-send-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer le test
          </button>
          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">Les envois de test n'affectent aucun compte ni aucun flux d'approbation.</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))]">
        <div className="border-b border-white/5 px-4 py-3 text-sm"><span className="text-muted-foreground">Objet :</span> <span className="font-medium">{tpl?.subject}</span></div>
        <iframe title="email-preview" data-testid="email-preview-frame" srcDoc={tpl?.html}
          className="h-[560px] w-full bg-white" sandbox="" />
      </div>
    </div>
  );
}

function DeliveryTab({ isSuper }) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["email-events"],
    queryFn: async () => (await api.get("/admin/email/events")).data,
  });
  const [triggering, setTriggering] = useState(false);

  const sendReport = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post("/admin/email/send-weekly-report");
      toast.success(`Rapport envoyé à ${data.sent} Super Admin · ${data.period}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi du rapport");
    } finally { setTriggering(false); }
  };

  if (isLoading) return <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const stats = data?.stats || {};
  const cards = [
    ["total", "Total", "text-foreground"], ["delivered", "Livrés", "text-emerald-400"],
    ["sent", "Envoyés", "text-sky-400"], ["bounced", "Rejetés", "text-red-400"],
    ["complained", "Plaintes", "text-orange-400"], ["failed", "Échecs", "text-red-400"],
  ];

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid flex-1 grid-cols-3 gap-3 sm:grid-cols-6">
          {cards.map(([k, label, cls]) => (
            <div key={k} data-testid={`email-stat-${k}`} className="rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-3">
              <p className={`font-heading text-2xl font-bold ${cls}`}>{stats[k] || 0}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => refetch()} data-testid="email-refresh-btn" className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Actualiser
        </button>
        {isSuper && (
          <button onClick={sendReport} disabled={triggering} data-testid="email-weekly-report-btn" className="flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm text-primary hover:bg-primary/25 disabled:opacity-50">
            {triggering ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Envoyer le rapport hebdo maintenant
          </button>
        )}
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Destinataire</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Statut</th><th className="px-4 py-3">Dernier événement</th><th className="px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {(data?.events || []).map((e) => (
              <tr key={e.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`email-event-${e.id}`}>
                <td className="px-4 py-3">{e.to}</td>
                <td className="px-4 py-3 text-muted-foreground">{KIND_FR[e.kind] || e.kind}</td>
                <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{e.last_event || "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {(!data?.events || data.events.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">Aucun e-mail enregistré pour l'instant.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
